// ============================================================
// TILT — POWERS  (the single source of truth)
// ------------------------------------------------------------
// THE LAW OF THIS FILE (the PackArt.js of TILT):
//   Every power is defined HERE and nowhere else. No component
//   hardcodes a power's cost, duration, icon, or effect. The HUD
//   reads this. The engine reads this. The NPC reads this. The
//   shop reads this. One edit, everything moves.
//
// ── WHAT CHANGED (AENERGY CONFORMANCE, Unit 1) ───────────────
// The registry is now the UNIVERSAL POWER OBJECT (Aenergy.js §11).
// Every entry carries: kind / zone / level / khaoCost / animaCost
// / cadence / ssg. Five kinds live here now:
//
//   impera   — the 12 zone-bound skills. THE OLD SEVEN POWERS,
//              PROMOTED, plus five new. Earned in-run by climbing
//              the ladder; Soul-bound to the zone that granted
//              them. Cost Khao. All OPT.
//   tome     — the Grimoire. Bought with tiltTokens, slotted
//              before a game (TOME_SLOTS). Ruined after one use
//              unless cadence-tagged (THE RUIN LAW, R39).
//   uhlti    — four. Lord-gated, Natural only. Khao AND Anima.
//   affinity — four, ever. Khao 0. Max 1 per RUN. All DENIAL.
//   prophecy — declared before launch. An alternate victory.
//
// ── DESIGN LAW, AMENDED: POWERS ARE VERBS AGAINST PHYSICS ────
// ...OR against the Aenergy stats (Lyt, Lyf, Anima, Khao).
// A power is NEVER "activate → gain points." It touches position,
// velocity, geometry, time, or a named Aenergy stat. If a proposed
// power touches none of those, it's a scoring rule and it belongs
// in the table file. (Amended because Diamond Skin and Wyrmheart
// are stat verbs — Lyf and Lyt are physics now; they're just the
// physics of the METAPHYSICS.)
//
// ── COSTS: TWO LEDGERS DURING THE UNIT-1 WINDOW ──────────────
//   khaoCost   — the REAL cost. Integer Khao. What Enforcer/the
//                new engine (Unit 2) charge.
//   cost       — ⚠ LEGACY. The old 0–100 charge cost, kept ONLY
//                so today's TiltEngine keeps working between the
//                Unit-1 and Unit-2 deploys. DIES WITH UNIT 2.
//                Nothing new may read it.
//
// ── EFFECTS: TWO RESOLUTION PATHS ────────────────────────────
//   apply(ctx)/onEnemy(ctx) — physics verbs, resolved right here
//     against the World. The Impera keep these, byte-for-byte
//     where they already existed.
//   effect: "key" — engine-resolved. Tomes/U'hltis/Affinities
//     that touch ENGINE state (Anima, the Dark, a mode, a window)
//     declare a key; TiltEngine's effect switch (Unit 2) owns the
//     mutation. A power never reaches into engine state directly —
//     same reason the renderer never draws from inside apply().
//
// ── TARGETING ────────────────────────────────────────────────
//   "self"   — fires on your ball. No aim step.
//   "point"  — needs a table coordinate. UI enters AIM MODE.
//   "enemy"  — a SABOTAGE. Crosses the wire via SabotageBus.
//   "window" — legal ONLY inside a Response Window (Enforcer:
//              RESPOND). These are the answers.
//   null     — not directly cast (Prophecies; passive Curio-likes).
// ============================================================

import { MagnetField, GravityZone, Seg, MAX_SPEED } from "./physics/World";
import { len, norm, clamp } from "./physics/Vec";
import { LEVEL } from "./Aenergy";

// ══════════════════════════════════════════════════════════════
// LEGACY CHARGE ECONOMY — ⚠ UNIT-1 COMPATIBILITY SHIMS.
// ------------------------------------------------------------
// Kept alive so TiltEngine/HUD/progression run unmodified between
// the Unit-1 and Unit-2 deploys. In the Aenergy engine, the
// chargeTable numbers below become the AENERGY tables (identical
// values — they condense to Khao at AENERGY_PER_KHAO:1), and
// CHARGE_MAX/chargeFor are retired. DO NOT build on these.
// ══════════════════════════════════════════════════════════════
export const CHARGE_MAX = 100;                       // ⚠ legacy — dies with Unit 2

export const CHARGE_RULES = {                        // survives as the AENERGY rules
  bumper:     3,
  slingshot:  2,
  target:     5,
  target_lit: 8,
  ramp:       12,
  orbit:      10,
  spinner:    1,
  jackpot:    25,
  drain:     -15,
};

export const chargeFor = (tag, table = null) => {
  const t = table?.chargeTable || CHARGE_RULES;
  return t[tag] ?? CHARGE_RULES[tag] ?? 0;
};

// ══════════════════════════════════════════════════════════════
// ⭐ THE TWELVE IMPERA — one per zone per tier.
// ------------------------------------------------------------
// The Lattice, as skills:
//
//            BEB (L1)        CHAMPION (L2)     LORD (L3)
//   space    Magnetize       Natural Glow      Radiant Anima
//   phantom  Ghost           Reverse           Split
//   time     Freeze          Rewind            Overclock
//   dragon   Diamond Skin    Anchor            Wyrmheart
//
// Seven of these are the old powers, physics untouched. Five are
// new. All are OPT (once per BALL), all cost Khao, all are earned
// by Ascending their zone and Soul-bound to it — knock the zone
// down and the Impera sleeps with it.
// ══════════════════════════════════════════════════════════════
export const POWERS = {

  // ────────────────────────── SPACE ──────────────────────────

  // ── MAGNETIZE — Space Beb ───────────────────────────────────
  // Point at a spot. The ball is pulled there. Gravity is Space's
  // whole personality. The single most PUZZLE-ENABLING power in
  // the kit. (Physics unchanged from the original.)
  magnetize: {
    id: "magnetize", name: "Magnetize", glyph: "◎",
    kind: "impera", zone: "space", level: 1, unlock: { level: LEVEL.BEB },
    khaoCost: 2, animaCost: 0, cadence: "OPT", ssg: false,
    cost: 30,                                        // ⚠ legacy
    target: "point", sabotage: true, duration: 2.2, tint: "gold",
    blurb: "Name a point. The ball wants it.",
    apply: ({ world, point }) => {
      if (!point) return null;
      world.addField(MagnetField(point.x, point.y, {
        radius: 150, strength: 1050, ttl: POWERS.magnetize.duration,
      }));
      return { fx: "magnet", at: { ...point }, r: 150,
               ttl: POWERS.magnetize.duration };
    },
    // SABOTAGE: drop a magnet over their OUTLANE. Watch them fight it.
    onEnemy: ({ world, point }) => {
      if (!point) return null;
      world.addField(MagnetField(point.x, point.y, {
        radius: 130, strength: 900, ttl: 2.0,
      }));
      return { fx: "magnet_hostile", at: { ...point }, r: 130, ttl: 2.0 };
    },
  },

  // ── NATURAL GLOW — Space Champion (NEW) ─────────────────────
  // The ball is briefly INTANGIBLE — it passes through posts and
  // slings (anything round), but NOT walls. Ghost is a key for
  // authored doors; Glow is starlight through the furniture. Two
  // different verbs: Ghost asks the table's permission, Glow
  // doesn't ask the posts'.
  natural_glow: {
    id: "natural_glow", name: "Natural Glow", glyph: "✧",
    kind: "impera", zone: "space", level: 2, unlock: { level: LEVEL.CHAMPION },
    khaoCost: 2, animaCost: 0, cadence: "OPT", ssg: false,
    cost: 25,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 1.2, tint: "ice",
    blurb: "Starlight doesn't argue with furniture.",
    apply: ({ ball }) => {
      if (!ball) return null;
      ball.intangible = POWERS.natural_glow.duration;   // solver: skip circle colliders
      return { fx: "glow", at: { ...ball.p }, ttl: POWERS.natural_glow.duration };
    },
  },

  // ── RADIANT ANIMA — Space Lord (NEW) ────────────────────────
  // NULLIFY ONE DRAIN. Armed, it waits; the next ball that dies,
  // doesn't. The top of the ladder is denial, not damage — this is
  // that law made playable. Engine-resolved: the drain interception
  // lives where drains live.
  radiant_anima: {
    id: "radiant_anima", name: "Radiant Anima", glyph: "☀",
    kind: "impera", zone: "space", level: 3, unlock: { level: LEVEL.LORD },
    khaoCost: 3, animaCost: 0, cadence: "OPT", ssg: false,
    cost: 40,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 0, tint: "gold",
    blurb: "The next drain is refused.",
    effect: "nullify_drain",
  },

  // ───────────────────────── PHANTOM ─────────────────────────

  // ── GHOST — Phantom Beb ─────────────────────────────────────
  // The ball phases through anything flagged `ghostable`. Tables
  // author which walls are ghostable — so Ghost isn't a universal
  // noclip, it's a KEY, and the table is the lock. The backbone of
  // the Trial tables. (Physics unchanged.)
  ghost: {
    id: "ghost", name: "Ghost", glyph: "◇",
    kind: "impera", zone: "phantom", level: 1, unlock: { level: LEVEL.BEB },
    khaoCost: 2, animaCost: 0, cadence: "OPT", ssg: false,
    cost: 25,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 1.4, tint: "silver",
    blurb: "Some walls are suggestions.",
    apply: ({ ball }) => {
      if (!ball) return null;
      ball.ghost = POWERS.ghost.duration;
      return { fx: "ghost", at: { ...ball.p } };
    },
  },

  // ── REVERSE — Phantom Champion ──────────────────────────────
  // Invert the vector. That drain becomes an escape. Cheap,
  // instant, and NEVER strictly good — a power you can misuse is
  // a power with a skill ceiling. (Physics unchanged.)
  reverse: {
    id: "reverse", name: "Reverse", glyph: "⇄",
    kind: "impera", zone: "phantom", level: 2, unlock: { level: LEVEL.CHAMPION },
    khaoCost: 1, animaCost: 0, cadence: "OPT", ssg: false,
    cost: 15,                                        // ⚠ legacy
    target: "self", sabotage: true, duration: 0, tint: "teal",
    blurb: "Down becomes up. Pray you meant it.",
    apply: ({ ball }) => {
      if (!ball) return null;
      ball.vel.x = -ball.vel.x;
      ball.vel.y = -ball.vel.y;
      return { fx: "reverse", at: { ...ball.p } };
    },
    onEnemy: ({ balls }) => {
      // Flips EVERY live ball on their table. In multiball this is
      // a catastrophe. That's the point.
      let any = false;
      for (const b of balls) {
        if (!b.alive) continue;
        b.vel.x = -b.vel.x; b.vel.y = -b.vel.y;
        any = true;
      }
      return any ? { fx: "reverse_hostile" } : null;
    },
  },

  // ── SPLIT — Phantom Lord ────────────────────────────────────
  // Two balls. Both yours. Both live. Both DRAINING. The phantom
  // twin — greed with no safety net. Promoted to the Phantom
  // Lord slot: the most dangerous Impera should be the deepest
  // one on the ladder. (Physics unchanged.)
  split: {
    id: "split", name: "Split", glyph: "⑂",
    kind: "impera", zone: "phantom", level: 3, unlock: { level: LEVEL.LORD },
    khaoCost: 4, animaCost: 0, cadence: "OPT", ssg: false,
    cost: 45,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 0, tint: "violet",
    blurb: "One ball becomes two. Twice the score. Twice the panic.",
    apply: ({ world, ball, engine }) => {
      if (!ball) return null;
      const spd = len(ball.vel) || 90;
      const ang = Math.atan2(ball.vel.y, ball.vel.x);
      const spread = 0.42;

      // The original takes the left fork...
      const a1 = ang - spread;
      ball.vel = { x: Math.cos(a1) * spd, y: Math.sin(a1) * spd };

      // ...and its twin takes the right. Same speed, mirrored.
      const a2 = ang + spread;
      const twin = engine.spawnBall(
        ball.p.x, ball.p.y,
        Math.cos(a2) * spd, Math.sin(a2) * spd,
        { owner: ball.owner }
      );
      twin.speedMul = ball.speedMul;
      return { fx: "split", at: { ...ball.p } };
    },
  },

  // ─────────────────────────── TIME ──────────────────────────

  // ── FREEZE — Time Beb ───────────────────────────────────────
  // The "oh god" button. Velocity is PRESERVED, not zeroed — when
  // it thaws the ball resumes its exact vector. Freeze buys you
  // TIME, not a free redirect. A held breath, not a teleport.
  // (Physics unchanged. And note: the Response Window is this
  // exact mechanism, engine-invoked — Freeze was the Window's
  // prototype all along.)
  freeze: {
    id: "freeze", name: "Freeze", glyph: "❄",
    kind: "impera", zone: "time", level: 1, unlock: { level: LEVEL.BEB },
    khaoCost: 1, animaCost: 0, cadence: "OPT", ssg: false,
    cost: 20,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 1.6, tint: "ice",
    blurb: "The ball stops. The world doesn't. Reposition.",
    apply: ({ ball }) => {
      if (!ball) return null;
      ball.frozen = POWERS.freeze.duration;
      return { fx: "freeze", at: { ...ball.p } };
    },
  },

  // ── REWIND — Time Champion (NEW) ────────────────────────────
  // The ball snaps back along its own trail — roughly a second
  // into its past — arriving at rest for a heartbeat before
  // gravity resumes its argument. The undo button, priced so you
  // can't live in it. Uses the trail the ball already keeps for
  // rendering: the memory was always there; Time just reads it.
  rewind: {
    id: "rewind", name: "Rewind", glyph: "↺",
    kind: "impera", zone: "time", level: 2, unlock: { level: LEVEL.CHAMPION },
    khaoCost: 2, animaCost: 0, cadence: "OPT", ssg: false,
    cost: 30,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 0, tint: "violet",
    blurb: "That didn't happen.",
    apply: ({ ball }) => {
      if (!ball || !ball.trail || ball.trail.length === 0) return null;
      const past = ball.trail[0];                    // the oldest remembered point
      const from = { ...ball.p };
      ball.p = { x: past.x, y: past.y };
      // A rewound ball arrives SLOW — a tenth of its speed, same
      // heading. Full speed would make Rewind a teleporting cannon;
      // zero would wedge it dead against whatever it rewound into.
      ball.vel = { x: ball.vel.x * 0.1, y: ball.vel.y * 0.1 };
      return { fx: "rewind", from, at: { ...ball.p } };
    },
  },

  // ── OVERCLOCK — Time Lord ───────────────────────────────────
  // THE GREED BUTTON. 2x speed, 2x score, 2x drain risk — and now,
  // per the Lyt law, +1 Lyt while it runs (Aenergy.lytFor reads
  // ball.speedMul > 1). Time's Lord doesn't stop the clock; it
  // spends it. (Physics unchanged.)
  overclock: {
    id: "overclock", name: "Overclock", glyph: "⚡",
    kind: "impera", zone: "time", level: 3, unlock: { level: LEVEL.LORD },
    khaoCost: 2, animaCost: 0, cadence: "OPT", ssg: false,
    cost: 30,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 6.0, tint: "red",
    blurb: "Everything doubles. Including the mistakes.",
    apply: ({ ball, engine }) => {
      if (!ball) return null;
      ball.speedMul = 1.85;
      engine.addTimer(POWERS.overclock.duration, () => { ball.speedMul = 1; });
      return { fx: "overclock", ttl: POWERS.overclock.duration };
    },
  },

  // ────────────────────────── DRAGON ─────────────────────────

  // ── DIAMOND SKIN — Dragon Beb (NEW) ─────────────────────────
  // For a spell, the Dragon zone's devices wear +2 Lyf — and pay
  // DOUBLE when Fractured. Armor the hoard, then crack it. A bet
  // against your own combo: if you can't get hot enough to break
  // the thicker skin before it lapses, you armored the table
  // against yourself for free. Engine-resolved (Lyf lives on
  // engine device state, not in the World).
  diamond_skin: {
    id: "diamond_skin", name: "Diamond Skin", glyph: "◆",
    kind: "impera", zone: "dragon", level: 1, unlock: { level: LEVEL.BEB },
    khaoCost: 2, animaCost: 0, cadence: "OPT", ssg: false,
    cost: 25,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 12.0, tint: "ice",
    blurb: "Armor the hoard. Then crack it.",
    effect: "diamond_skin",
    power: 2,                                        // the +Lyf magnitude
  },

  // ── ANCHOR — Dragon Champion ────────────────────────────────
  // THE PUZZLE POWER, and the best sabotage in the game. You place
  // a WALL. Level design in real time. A dragon doesn't dodge the
  // world; it renovates. (Physics unchanged.)
  anchor: {
    id: "anchor", name: "Anchor", glyph: "▬",
    kind: "impera", zone: "dragon", level: 2, unlock: { level: LEVEL.CHAMPION },
    khaoCost: 3, animaCost: 0, cadence: "OPT", ssg: false,
    cost: 35,                                        // ⚠ legacy
    target: "point", sabotage: true, duration: 6.0, tint: "gold",
    blurb: "Place a wall. The table is yours to argue with.",
    apply: ({ world, point, engine, ball }) => {
      if (!point) return null;
      // The wall lies PERPENDICULAR to the ball's approach — a wall
      // you can actually bank off, not one the ball slides along.
      // If there's no ball (rare), default to horizontal.
      const dir = ball && len(ball.vel) > 1
        ? norm(ball.vel)
        : { x: 1, y: 0 };
      const t = { x: -dir.y, y: dir.x };   // perpendicular
      const half = 30;
      const seg = Seg(
        point.x - t.x * half, point.y - t.y * half,
        point.x + t.x * half, point.y + t.y * half,
        { tag: "anchor", thickness: 6, restitution: 0.72,
          id: `anchor_${engine.nextId()}` }
      );
      engine.addTempCollider(seg, POWERS.anchor.duration);
      return { fx: "anchor", a: { x: seg.a.x, y: seg.a.y },
               b: { x: seg.b.x, y: seg.b.y },
               ttl: POWERS.anchor.duration };
    },
    // SABOTAGE: a HORIZONTAL bar — the shape most likely to block
    // a ramp mouth or a lane entrance. Shorter fuse (it's rude).
    onEnemy: ({ world, point, engine }) => {
      if (!point) return null;
      const half = 34;
      const seg = Seg(
        point.x - half, point.y, point.x + half, point.y,
        { tag: "anchor_hostile", thickness: 6, restitution: 0.5,
          id: `hanchor_${engine.nextId()}` }
      );
      engine.addTempCollider(seg, 4.5);
      return { fx: "anchor_hostile",
               a: { x: seg.a.x, y: seg.a.y }, b: { x: seg.b.x, y: seg.b.y },
               ttl: 4.5 };
    },
  },

  // ── WYRMHEART — Dragon Lord (NEW) ───────────────────────────
  // +2 Lyt, flat, for the duration. Not faster — HOTTER. Where
  // Overclock earns its heat by risking speed, Wyrmheart simply
  // burns: a cold ball that hits like a comet. The Lyf-Cycle
  // key: this is how you crack a high guard WITHOUT a live combo.
  // Engine-resolved (Lyt is computed in _award; the engine reads
  // engine.lytBonus while this runs).
  wyrmheart: {
    id: "wyrmheart", name: "Wyrmheart", glyph: "♥",
    kind: "impera", zone: "dragon", level: 3, unlock: { level: LEVEL.LORD },
    khaoCost: 3, animaCost: 0, cadence: "OPT", ssg: false,
    cost: 40,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 8.0, tint: "ember",
    blurb: "Not faster. Hotter.",
    effect: "lyt_bonus",
    power: 2,
  },

  // ══════════════════════════════════════════════════════════
  // 📖 TOMES — the Grimoire. Bought with tiltTokens (they finally
  // have a job), slotted before a game. Ruined after one use
  // unless tagged (THE RUIN LAW). The six Anchor Tomes travel the
  // whole Empire — same names, same shapes, every IE game.
  // ══════════════════════════════════════════════════════════

  // ── RAYS OF HOPE — the Nihil ────────────────────────────────
  // The answer. NULLIFIES a table declaration inside the Window.
  // L3: it pre-empts anything (canPreempt always true at L3).
  rays_of_hope: {
    id: "rays_of_hope", name: "Rays of Hope", glyph: "☄",
    kind: "tome", zone: null, level: 3,
    khaoCost: 2, animaCost: 0, cadence: null, ssg: false,
    cost: 30,                                        // ⚠ legacy
    target: "window", counter: true, sabotage: false, duration: 0, tint: "gold",
    blurb: "Nihil. The mode is refused.",
    effect: "nihil",
  },

  // ── REGNANT — the Omnia ─────────────────────────────────────
  // The greedier answer. DESTROYS the declaration AND takes its
  // points. Costs Anima too — Omnia always draws a little blood.
  regnant: {
    id: "regnant", name: "Regnant", glyph: "♛",
    kind: "tome", zone: null, level: 3,
    khaoCost: 2, animaCost: 2, cadence: null, ssg: false,
    cost: 35,                                        // ⚠ legacy
    target: "window", counter: true, sabotage: false, duration: 0, tint: "violet",
    blurb: "Omnia. The mode is devoured.",
    effect: "omnia",
  },

  // ── BHODI TREE — +1 Anima, flat ─────────────────────────────
  bhodi_tree: {
    id: "bhodi_tree", name: "Bhodi Tree", glyph: "🜃",
    kind: "tome", zone: null, level: 1,
    khaoCost: 1, animaCost: 0, cadence: null, ssg: false,
    cost: 15,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 0, tint: "toxic",
    blurb: "Breathe. +1 Anima.",
    effect: "gain_anima",
    power: 1,
  },

  // ── KHAO GARDEN — +1 Max Khao (the only road past 10) ───────
  khao_garden: {
    id: "khao_garden", name: "Khao Garden", glyph: "❀",
    kind: "tome", zone: null, level: 2,
    khaoCost: 0, animaCost: 2, cadence: null, ssg: false,
    cost: 20,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 0, tint: "toxic",
    blurb: "Tend the garden. The ceiling rises.",
    effect: "grow_max_khao",
    power: 1,
  },

  // ── RESTRAINING ORDEAL — ★ THE ATV. The Empire signature. ───
  // A covenant: every ball you spend ZERO Khao, +1 Anima. The
  // INSTANT you spend Khao, the covenant breaks and the Tome is
  // RUINED. It stacks with the Growth Law — a clean ball pays
  // +1 Max Khao AND +1 Anima — and the table's whole job becomes
  // tempting you into breaking your own vow. The adversary wins
  // by tempting you. No other game does this.
  restraining_ordeal: {
    id: "restraining_ordeal", name: "Restraining Ordeal", glyph: "⚖",
    kind: "tome", zone: null, level: 2,
    khaoCost: 0, animaCost: 0, cadence: "ATV", ssg: false,
    cost: 0,                                         // ⚠ legacy
    target: "self", sabotage: false, duration: 0, tint: "bone",
    blurb: "A vow: touch no Khao, and be paid in life. Break it, and it's ash.",
    effect: "atv_ordeal",
  },

  // ── SNAKE EYES — ★ THE ACB + SSG. ───────────────────────────
  // THE TABLE NAMES THE NUMBER (1–6). Your next N shots pay ×N.
  // Drain before landing N of them and dim 5 Anima. In solo, the
  // NPC brain picks the N that hurts most — it is not random, it
  // is a decision made against you. Wins and losses feed the
  // Shadow Gambling streak (Ace of Shades).
  snake_eyes: {
    id: "snake_eyes", name: "Snake Eyes", glyph: "⚂",
    kind: "tome", zone: null, level: 2,
    khaoCost: 1, animaCost: 0, cadence: null, ssg: true,
    cost: 20,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 0, tint: "hot",
    blurb: "The table picks the number. You live with it.",
    effect: "acb_snake_eyes",
  },

  // ── THE DEVIL'S ADVOCATE — the Contract. ────────────────────
  // BOTH sides gain: you take +3 Anima; the table takes a level
  // on its OWN ladder (§ the Inversion — the table ascends too).
  // Feeding the adversary for a price is either genius or the
  // Tablet of N'ullus strategy in disguise. Sometimes both.
  devils_advocate: {
    id: "devils_advocate", name: "The Devil's Advocate", glyph: "⚶",
    kind: "tome", zone: null, level: 2,
    khaoCost: 1, animaCost: 0, cadence: null, ssg: false,
    cost: 20,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 0, tint: "ember",
    blurb: "Both sides profit. One of you is wrong about that.",
    effect: "contract_advocate",
  },

  // ══════════════════════════════════════════════════════════
  // ◈ THE FOUR U'HLTIS — the jackpots. Natural Lord only. They
  // charge Khao AND Anima — the only class that does, and that's
  // what makes them U'hltis. Firing one, you WATCH A BALL VANISH
  // FROM THE RACK. They should feel like a war crime.
  // ══════════════════════════════════════════════════════════

  super_nova: {
    id: "super_nova", name: "Super Nova", glyph: "✦",
    kind: "uhlti", zone: "space", level: 3,
    khaoCost: 8, animaCost: 4, cadence: null, ssg: false,
    cost: 80,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 20.0, tint: "gold",
    blurb: "The Space shot pays like the end of a star. For a while.",
    effect: "uhlti_super_nova",
  },

  royal_darkness: {
    id: "royal_darkness", name: "Royal Darkness", glyph: "♞",
    kind: "uhlti", zone: "phantom", level: 3,
    khaoCost: 4, animaCost: 6, cadence: null, ssg: false,
    cost: 70,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 0, tint: "violet",
    blurb: "Dim the table's Lyf by 2. Its guard opens.",
    effect: "uhlti_royal_darkness",
    power: 2,
  },

  king_of_the_hill: {
    id: "king_of_the_hill", name: "King of the Hill", glyph: "♔",
    kind: "uhlti", zone: "time", level: 3,
    khaoCost: 7, animaCost: 8, cadence: null, ssg: false,
    cost: 90,                                        // ⚠ legacy
    target: "self", sabotage: false, duration: 10.0, tint: "gold",
    blurb: "Everything is the jackpot. Ten seconds. Go.",
    effect: "uhlti_king_of_the_hill",
  },

  show_stomper: {
    id: "show_stomper", name: "Show Stomper", glyph: "☠",
    kind: "uhlti", zone: "dragon", level: 3,
    khaoCost: 10, animaCost: 10, cadence: null, ssg: false,
    cost: 100,                                       // ⚠ legacy
    target: "self", sabotage: false, duration: 0, tint: "ember",
    blurb: "Dim the TABLE's Anima by 10. An entire life, spent on spite.",
    effect: "uhlti_show_stomper",
    power: 10,
  },

  // ══════════════════════════════════════════════════════════
  // ✦ THE FOUR AFFINITIES — ever. Khao 0: they cost nothing but
  // everything before them (a zone with its U'hlti already hit).
  // Max ONE per run. All OP2. ALL DENIAL — the top of the ladder
  // takes things away; it does not add them.
  // ══════════════════════════════════════════════════════════

  ytyranus: {
    id: "ytyranus", name: "Y'tyranus", glyph: "✝",
    kind: "affinity", zone: null, level: 3,
    khaoCost: 0, animaCost: 0, cadence: "OP2", ssg: false,
    cost: 0,                                         // ⚠ legacy
    target: "self", sabotage: false, duration: 0, tint: "gold",
    blurb: "Holy. Nullify one drain.",
    effect: "nullify_drain",
  },

  xaol: {
    id: "xaol", name: "X'aol", glyph: "✖",
    kind: "affinity", zone: null, level: 3,
    khaoCost: 0, animaCost: 0, cadence: "OP2", ssg: false,
    cost: 0,                                         // ⚠ legacy
    target: "self", sabotage: false, duration: 0, tint: "hot",
    blurb: "Evil. Destroy one of the table's lit shots — deny it its escalation.",
    effect: "aff_xaol",
  },

  enlightenment: {
    id: "enlightenment", name: "Enlightenment", glyph: "☸",
    kind: "affinity", zone: null, level: 3,
    khaoCost: 0, animaCost: 0, cadence: "OP2", ssg: false,
    cost: 0,                                         // ⚠ legacy
    target: "window", counter: true, sabotage: false, duration: 0, tint: "ice",
    blurb: "Awoken. Nullify one declaration — or, in a duel, a rival Affinity.",
    effect: "aff_enlightenment",
  },

  friend_or_faux: {
    id: "friend_or_faux", name: "Friend or Faux", glyph: "🃏",
    kind: "affinity", zone: null, level: 3,
    khaoCost: 0, animaCost: 0, cadence: "OP2", ssg: true,
    cost: 0,                                         // ⚠ legacy
    target: "self", sabotage: false, duration: 0, tint: "violet",
    blurb: "J'aeonxi. Fetch a Shadow Gambling. No repeats back-to-back.",
    effect: "aff_friend_or_faux",
  },

  // ══════════════════════════════════════════════════════════
  // 🔮 PROPHECIES — declared before launch (Enforcer: PROPHECY),
  // immune to everything afterward (R74). An alternate victory.
  // `condition` is a predicate over the engine the engine itself
  // evaluates at end of every ball — the Prophecy never reaches in.
  // ══════════════════════════════════════════════════════════

  space_race: {
    id: "space_race", name: "Space Race", glyph: "🚀",
    kind: "prophecy", zone: "space", level: 3,
    khaoCost: 15, animaCost: 0, cadence: null, ssg: false,
    cost: 0,                                         // ⚠ legacy
    target: null, sabotage: false, duration: 0, tint: "gold",
    blurb: "Two Supreme zones, one of them titled and Space. Instant win.",
    effect: "prophecy_space_race",
  },

  // ★ THE INVERSION — TILT's Tablet of N'ullus. The table ascends
  // too (Foundry's heat ladder IS an Escling becoming a Lord).
  // Declare this, and you are deliberately RAISING the boss —
  // stoking the furnace on purpose, letting it beat you correctly.
  // If the TABLE reaches Lord twice, YOU win.
  tablet_of_nullus: {
    id: "tablet_of_nullus", name: "The Tablet of N'ullus", glyph: "▽",
    kind: "prophecy", zone: null, level: 3,
    khaoCost: 15, animaCost: 0, cadence: null, ssg: false,
    cost: 0,                                         // ⚠ legacy
    target: null, sabotage: false, duration: 0, tint: "bone",
    blurb: "If the table achieves its ambition, YOU win. Feed it.",
    effect: "prophecy_inversion",
  },

  ace_of_shades: {
    id: "ace_of_shades", name: "Ace of Shades", glyph: "🂡",
    kind: "prophecy", zone: null, level: 3,
    khaoCost: 15, animaCost: 0, cadence: null, ssg: true,
    cost: 0,                                         // ⚠ legacy
    target: null, sabotage: false, duration: 0, tint: "hot",
    blurb: "Win seven Shadow Gamblings in a row. Instant win.",
    effect: "prophecy_ace_of_shades",
  },

  // ══════════════════════════════════════════════════════════
  // ↯ NUDGE — the ancestor. Outside every class, before every
  // ladder. FREE, never occupies a slot, builds TILT WARNING.
  // The one power that costs nothing and can still end you.
  // (kind "impera", zone null: universal, unearned, always on.)
  // ══════════════════════════════════════════════════════════
  nudge: {
    id: "nudge", name: "Nudge", glyph: "↯",
    kind: "impera", zone: null, level: 1, unlock: { level: LEVEL.ESCLING },
    khaoCost: 0, animaCost: 0, cadence: null, ssg: false,
    cost: 0,
    target: "self", sabotage: false, duration: 0, tint: "vellum",
    blurb: "Free. Not without cost.",
    apply: ({ balls, engine, dir }) => {
      const d = dir || { x: 0, y: -1 };
      const IMPULSE = 78;
      for (const b of balls) {
        if (!b.alive || b.frozen > 0) continue;
        b.vel.x += d.x * IMPULSE;
        b.vel.y += d.y * IMPULSE;
      }
      engine.addTiltWarning();
      return { fx: "nudge", dir: d };
    },
  },
};

// ══════════════════════════════════════════════════════════════
// LOOKUPS — unchanged surface, richer registry behind it.
// ══════════════════════════════════════════════════════════════
export const POWER_LIST = Object.values(POWERS);
export const getPower = (id) => POWERS[id] || null;
export const isPower  = (id) => !!POWERS[id];

export const byKind    = (kind) => POWER_LIST.filter((p) => p.kind === kind);
export const IMPERA    = byKind("impera").filter((p) => p.zone);   // nudge excluded — it's the ancestor
export const TOMES     = byKind("tome");
export const UHLTIS    = byKind("uhlti");
export const AFFINITIES = byKind("affinity");
export const PROPHECIES = byKind("prophecy");

/** The Impera a zone grants at a level. One per zone per tier —
 *  imperaFor("dragon", 2) → Anchor. The Lattice, queryable. */
export const imperaFor = (zone, level) =>
  IMPERA.find((p) => p.zone === zone && p.level === level) || null;

/** The zone's U'hlti. Exactly one each; the Grimoire shows it
 *  greyed until the zone is a Natural Lord. */
export const uhltiFor = (zone) =>
  UHLTIS.find((p) => p.zone === zone) || null;

// Which powers may be aimed at an opponent (the sabotage kit).
export const SABOTAGE_POWERS = POWER_LIST.filter((p) => p.sabotage);

// ══════════════════════════════════════════════════════════════
// ⚠ LEGACY LOADOUT — UNIT-1 COMPATIBILITY. DIES WITH UNIT 6.
// ------------------------------------------------------------
// The old 4-power loadout keeps today's engine/screens/progression
// alive. Under the Aenergy engine the loadout becomes THE GRIMOIRE
// (TOME_SLOTS Tomes; Imperas are earned in-run, never slotted) —
// that is progression SCHEMA v2, deploy Unit 6. validateLoadout's
// behavior is unchanged so v1 saves keep validating.
// ══════════════════════════════════════════════════════════════
export const LOADOUT_SIZE = 4;
export const DEFAULT_LOADOUT = ["freeze", "magnetize", "anchor", "overclock"];

/** The Grimoire default — what a fresh save slots once Unit 6
 *  lands. Rays of Hope teaches the Window; the Ordeal teaches
 *  restraint; the rest teach the two resources. */
export const DEFAULT_GRIMOIRE = ["rays_of_hope", "restraining_ordeal", "bhodi_tree", "snake_eyes"];

export const validateLoadout = (ids = []) => {
  const clean = ids.filter(isPower).filter((id) => id !== "nudge");
  const out = [];
  for (const id of clean) {
    if (out.length >= LOADOUT_SIZE) break;
    if (!out.includes(id)) out.push(id);       // no duplicates
  }
  // Backfill from the default kit so a loadout is never short.
  for (const id of DEFAULT_LOADOUT) {
    if (out.length >= LOADOUT_SIZE) break;
    if (!out.includes(id)) out.push(id);
  }
  return out;
};

/** The Grimoire validator — Tomes only, TOME_SLOTS wide. Unit 6's
 *  progression v2 calls this; nothing calls it before then. */
export const validateGrimoire = (ids = []) => {
  const clean = ids.filter((id) => getPower(id)?.kind === "tome");
  const out = [];
  for (const id of clean) {
    if (out.length >= LOADOUT_SIZE) break;
    if (!out.includes(id)) out.push(id);
  }
  for (const id of DEFAULT_GRIMOIRE) {
    if (out.length >= LOADOUT_SIZE) break;
    if (!out.includes(id)) out.push(id);
  }
  return out;
};

export default POWERS;
